import React, { useState, useEffect, useCallback, useRef } from 'react';
import { base44 } from '@/api/base44Client';
import { useAuth } from '@/lib/AuthContext';
import { Link } from 'react-router-dom';
import {
  Loader2, Building2, Upload, FileText, CheckCircle, AlertCircle,
  ChevronDown, ChevronUp, ExternalLink, ArrowRight,
} from 'lucide-react';
import { useToast } from '@/components/ui/use-toast';
import B2BSignInScreen from '@/components/b2b/B2BSignInScreen';
import GettingStarted from '@/components/b2b/GettingStarted';
import InvitedScreen from '@/components/b2b/InvitedScreen';
import FulfilmentTracker from '@/components/b2b/FulfilmentTracker';
import { sortB2BOrders, fmtB2BOrderDate } from '@/lib/b2bOrderDate';

const STATUS_LABELS = {
  draft: 'Pending review',
  acknowledged: 'Acknowledged',
  in_production: 'In production',
  stock_allocated: 'Stock allocated',
  dispatched: 'Dispatched',
  invoiced: 'Invoiced',
  paid: 'Paid',
  cancelled: 'Cancelled',
  unallocated: 'Awaiting allocation',
};

const STATUS_COLORS = {
  draft: 'text-white/50 border-white/20',
  acknowledged: 'text-blue-400 border-blue-400/30',
  in_production: 'text-yellow-400 border-yellow-400/30',
  stock_allocated: 'text-cyan-400 border-cyan-400/30',
  dispatched: 'text-purple-400 border-purple-400/30',
  invoiced: 'text-orange-400 border-orange-400/30',
  paid: 'text-green-400 border-green-400/30',
  cancelled: 'text-red-400 border-red-400/30',
  unallocated: 'text-yellow-400 border-yellow-400/30',
};

export default function B2BPortal() {
  const { user, isLoadingAuth, navigateToLogin } = useAuth();
  const { toast } = useToast();
  const [account, setAccount] = useState(null);
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [dragging, setDragging] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [lastSubmission, setLastSubmission] = useState(null);
  const [expandedOrder, setExpandedOrder] = useState(null);
  const [accepting, setAccepting] = useState(false);
  const [acceptMsg, setAcceptMsg] = useState('');
  const dropRef = useRef(null);

  // Stash any invite token from the URL in localStorage so it survives the
  // post-login redirect (which can drop the query string on some setups).
  useEffect(() => {
    const t = new URLSearchParams(window.location.search).get('invite');
    if (t) { try { localStorage.setItem('b2b_invite_token', t); } catch (_e) {} }
  }, []);

  const load = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const res = await base44.functions.invoke('b2bGetMyAccount', {});
      if (res?.data?.success) {
        setAccount(res.data.account);
        setOrders(res.data.orders || []);
      } else {
        setError(res?.data?.error || 'Could not load your trade account.');
      }
    } catch (e) {
      setError(e?.response?.data?.error || e.message || 'Could not load your trade account.');
    }
    setLoading(false);
  }, []);

  // On sign-in: if an ?invite=<token> is present, accept it (links this login
  // to the trade account); otherwise just load the linked account + orders.
  useEffect(() => {
    if (!user) return;
    const urlToken = new URLSearchParams(window.location.search).get('invite');
    let token = urlToken;
    if (!token) { try { token = localStorage.getItem('b2b_invite_token'); } catch (_e) {} }
    if (token) {
      setAccepting(true);
      base44.functions.invoke('b2bAcceptInvite', { token })
        .then((res) => {
          if (res?.data?.success) {
            setAccount(res.data.account);
            setOrders(res.data.orders || []);
            setAcceptMsg(`Your trade account "${res.data.account.company_name}" is now linked to this login.`);
            const url = new URL(window.location.href);
            url.searchParams.delete('invite');
            window.history.replaceState({}, '', url.pathname + url.search);
          } else {
            setError(res?.data?.error || 'Could not accept this invitation.');
          }
        })
        .catch((e) => setError(e?.response?.data?.error || e.message || 'Could not accept this invitation.'))
        .finally(() => { try { localStorage.removeItem('b2b_invite_token'); } catch (_e) {} setAccepting(false); setLoading(false); });
    } else {
      load();
    }
  }, [user, load]);

  if (isLoadingAuth) {
    return (
      <div className="min-h-screen bg-background pt-24 flex items-center justify-center">
        <Loader2 className="w-6 h-6 animate-spin text-primary" />
      </div>
    );
  }

  if (!user) {
    return (
      <div className="min-h-screen bg-background pt-24 pb-20">
        <header className="py-10 px-6 lg:px-10 border-b border-white/5">
          <div className="max-w-5xl mx-auto">
            <div className="flex items-center gap-3 mb-2">
              <Building2 className="w-6 h-6 text-primary" />
              <p className="text-primary text-xs font-bold tracking-widest">B2B</p>
            </div>
            <h1 className="text-white font-black text-2xl sm:text-3xl uppercase">Trade Portal</h1>
            <p className="text-white/30 text-sm mt-1">
              Upload a purchase order to place an order with Motion Reality. We'll review it, allocate stock and send your invoice.
            </p>
          </div>
        </header>

        <section className="px-6 lg:px-10 py-8 max-w-5xl mx-auto">
          {new URLSearchParams(window.location.search).get('invite')
            ? <InvitedScreen token={new URLSearchParams(window.location.search).get('invite')} onSignIn={navigateToLogin} />
            : <GettingStarted onSignIn={navigateToLogin} />}
        </section>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background pt-24 pb-20">
      <header className="py-10 px-6 lg:px-10 border-b border-white/5">
        <div className="max-w-5xl mx-auto">
          <div className="flex items-center gap-3 mb-2">
            <Building2 className="w-6 h-6 text-primary" />
            <p className="text-primary text-xs font-bold tracking-widest">B2B</p>
          </div>
          <h1 className="text-white font-black text-2xl sm:text-3xl uppercase">Trade Portal</h1>
          <p className="text-white/30 text-sm mt-1">
            Upload a purchase order to place an order with Motion Reality. We'll review it, allocate stock and send your invoice.
          </p>
        </div>
      </header>

      <section className="px-6 lg:px-10 py-8 max-w-5xl mx-auto">
        {loading ? (
          <div className="flex justify-center py-20"><Loader2 className="w-6 h-6 animate-spin text-primary" /></div>
        ) : error ? (
          <div className="border border-yellow-400/30 bg-yellow-400/[0.03] p-8 text-center">
            <AlertCircle className="w-8 h-8 text-yellow-400 mx-auto mb-3" />
            <p className="text-white font-bold text-sm">{error}</p>
            <Link to="/contact" className="inline-flex items-center gap-1.5 mt-4 text-primary text-xs font-bold tracking-widest hover:underline">
              CONTACT US <ArrowRight className="w-3.5 h-3.5" />
            </Link>
          </div>
        ) : (
          <div className="space-y-8">
            {acceptMsg && (
              <div className="border border-green-400/30 bg-green-400/[0.03] p-4 flex items-center gap-3">
                <CheckCircle className="w-5 h-5 text-green-400 shrink-0" />
                <p className="text-white text-sm">{acceptMsg}</p>
              </div>
            )}
            {/* Account summary */}
            {account && (
              <div className="border border-white/10 bg-card p-6">
                <div className="flex items-center gap-3 mb-4">
                  <Building2 className="w-5 h-5 text-primary" />
                  <p className="text-white font-bold">{account.company_name}</p>
                  {account.account_code && <span className="text-white/40 text-xs font-mono border border-white/15 px-2 py-0.5">{account.account_code}</span>}
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 text-xs">
                  {account.contact_name && <div><p className="text-white/30 text-[10px] font-bold tracking-widest mb-0.5">CONTACT</p><p className="text-white/70">{account.contact_name}</p></div>}
                  {account.contact_email && <div><p className="text-white/30 text-[10px] font-bold tracking-widest mb-0.5">EMAIL</p><p className="text-white/70">{account.contact_email}</p></div>}
                  {account.contact_phone && <div><p className="text-white/30 text-[10px] font-bold tracking-widest mb-0.5">TELEPHONE</p><p className="text-white/70">{account.contact_phone}</p></div>}
                  {account.payment_terms_days && <div><p className="text-white/30 text-[10px] font-bold tracking-widest mb-0.5">PAYMENT TERMS</p><p className="text-white/70">Net {account.payment_terms_days} days</p></div>}
                  {account.address_line1 && (
                    <div className="sm:col-span-2"><p className="text-white/30 text-[10px] font-bold tracking-widest mb-0.5">REGISTERED ADDRESS</p>
                      <p className="text-white/70">{[account.address_line1, account.address_line2, account.address_city, account.address_county, account.address_postcode].filter(Boolean).join(', ')}</p>
                    </div>
                  )}
                  <div className="sm:col-span-2"><p className="text-white/30 text-[10px] font-bold tracking-widest mb-0.5">VERIFIED SUPPLIER</p><p className="text-white/70">Motion Reality Sim Racing Ltd · D-U-N-S 232788118</p></div>
                </div>
              </div>
            )}

            {/* Upload dropzone */}
            <div
              ref={dropRef}
              onDragOver={(e) => { e.preventDefault(); setDragging(true); }}
              onDragLeave={() => setDragging(false)}
              onDrop={handleDrop}
              className={`border-2 border-dashed rounded p-8 text-center transition-colors ${dragging ? 'border-primary bg-primary/5' : 'border-white/15 hover:border-white/30'}`}
            >
              {uploading || submitting ? (
                <div className="flex flex-col items-center gap-3">
                  <Loader2 className="w-8 h-8 animate-spin text-primary" />
                  <p className="text-white/50 text-sm font-bold tracking-widest">{submitting ? 'PROCESSING YOUR ORDER…' : 'UPLOADING…'}</p>
                  <p className="text-white/30 text-xs">{submitting ? 'Reading your purchase order' : 'Uploading your PDF'}</p>
                </div>
              ) : (
                <label className="cursor-pointer flex flex-col items-center gap-3">
                  <Upload className="w-8 h-8 text-white/30" />
                  <p className="text-white/70 font-bold text-sm tracking-widest">UPLOAD A PURCHASE ORDER</p>
                  <p className="text-white/30 text-xs max-w-md">Drop your PO PDF here or click to browse. We'll read it automatically and send it to our team for processing.</p>
                  <input type="file" accept="application/pdf" className="hidden" onChange={handleFileInput} />
                </label>
              )}
            </div>

            {/* Last submission confirmation */}
            {lastSubmission && (
              <div className="border border-green-400/30 bg-green-400/[0.03] p-6 space-y-3">
                <div className="flex items-center gap-2">
                  <CheckCircle className="w-5 h-5 text-green-400" />
                  <p className="text-green-400 text-[10px] font-bold tracking-widest">ORDER RECEIVED</p>
                </div>
                <p className="text-white font-bold text-lg">
                  {lastSubmission.order.po_number ? `PO ${lastSubmission.order.po_number}` : 'Your purchase order'} has been received
                </p>
                <p className="text-white/40 text-xs">
                  {(lastSubmission.order.line_items || []).length} line item{(lastSubmission.order.line_items || []).length === 1 ? '' : 's'} · £{(lastSubmission.order.subtotal_ex_vat || 0).toFixed(2)} ex. VAT · £{(lastSubmission.order.total_inc_vat || 0).toFixed(2)} inc. VAT
                </p>
                <p className="text-white/30 text-xs leading-relaxed">
                  Our team will review your order, allocate stock and email your invoice. You'll see the status update in your orders list below.
                </p>
                <button onClick={() => setLastSubmission(null)} className="text-white/40 hover:text-white text-[10px] font-bold tracking-widest">DISMISS</button>
              </div>
            )}

            {/* Orders list */}
            <div>
              <p className="text-white/40 text-[10px] font-bold tracking-widest mb-3">YOUR ORDERS</p>
              {orders.length === 0 ? (
                <div className="text-center py-12 text-white/30 border border-white/10">
                  <FileText className="w-10 h-10 mx-auto mb-3 opacity-30" />
                  <p className="text-sm font-bold tracking-widest">NO ORDERS YET</p>
                  <p className="text-xs mt-1">Upload a purchase order above to get started</p>
                </div>
              ) : (
                <div className="space-y-2">
                  {sortB2BOrders(orders).map(order => (
                    <PortalOrderRow
                      key={order.id}
                      order={order}
                      expanded={expandedOrder === order.id}
                      onToggle={() => setExpandedOrder(expandedOrder === order.id ? null : order.id)}
                    />
                  ))}
                </div>
              )}
            </div>
          </div>
        )}
      </section>
    </div>
  );

  async function handleFile(file) {
    if (!file || file.type !== 'application/pdf') {
      toast({ title: 'Please upload a PDF file', variant: 'destructive' });
      return;
    }
    setUploading(true);
    try {
      const up = await base44.integrations.Core.UploadFile({ file });
      if (!up?.file_url) throw new Error('Upload failed');
      setUploading(false);
      setSubmitting(true);
      const res = await base44.functions.invoke('b2bCustomerSubmitPO', { pdf_url: up.file_url });
      if (!res?.data?.success) throw new Error(res?.data?.error || 'Could not process your purchase order');
      setLastSubmission({ order: res.data.order, parsed: res.data.parsed });
      toast({ title: 'Order submitted', description: 'We\'ll review your PO and be in touch' });
      // Refresh orders list
      load();
    } catch (e) {
      toast({ title: 'Submission failed', description: e?.response?.data?.error || e.message, variant: 'destructive' });
    }
    setUploading(false);
    setSubmitting(false);
  }

  function handleDrop(e) {
    e.preventDefault();
    setDragging(false);
    const file = e.dataTransfer.files?.[0];
    handleFile(file);
  }

  function handleFileInput(e) {
    handleFile(e.target.files?.[0]);
    e.target.value = '';
  }
}

function PortalOrderRow({ order, expanded, onToggle }) {
  const totalLines = (order.line_items || []).length;
  const allocatedLines = (order.line_items || []).filter(li => li.stock_allocated).length;

  return (
    <div className="border border-white/10 bg-card">
      <div className="flex items-center gap-4 px-5 py-4 cursor-pointer hover:bg-white/[0.02] transition-colors" onClick={onToggle}>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-3 flex-wrap">
            {order.po_number && <p className="text-white font-bold text-sm font-mono">PO {order.po_number}</p>}
            <span className={`px-2 py-0.5 border text-[10px] font-bold tracking-widest uppercase ${STATUS_COLORS[order.status] || 'text-white/40 border-white/15'}`}>
              {STATUS_LABELS[order.status] || (order.status || 'draft').replace('_', ' ')}
            </span>
          </div>
          <p className="text-white/30 text-xs mt-0.5">
            {fmtB2BOrderDate(order)} · {totalLines} item{totalLines === 1 ? '' : 's'} · £{(order.subtotal_ex_vat || 0).toFixed(2)} ex. VAT
            {allocatedLines > 0 && <span className="ml-2 text-cyan-400">{allocatedLines}/{totalLines} allocated</span>}
          </p>
        </div>
        <div className="flex items-center gap-3 shrink-0">
          {order.invoice_url && (
            <a href={order.invoice_url} target="_blank" rel="noreferrer" onClick={e => e.stopPropagation()}
              className="inline-flex items-center gap-1.5 border border-white/15 hover:border-white/30 text-white/50 hover:text-white text-[10px] font-bold tracking-widest px-3 py-1.5 transition-colors">
              <FileText className="w-3.5 h-3.5" /> INVOICE
            </a>
          )}
          {expanded ? <ChevronUp className="w-4 h-4 text-white/30" /> : <ChevronDown className="w-4 h-4 text-white/30" />}
        </div>
      </div>

      {expanded && (
        <div className="border-t border-white/10 p-5 space-y-4">
          <FulfilmentTracker order={order} />
          <div>
            <p className="text-white/40 text-[10px] font-bold tracking-widest mb-2">LINE ITEMS</p>
            <div className="overflow-x-auto">
              <table className="w-full text-xs">
                <thead>
                  <tr className="border-b border-white/10">
                    <th className="text-white/30 text-left py-2 pr-3 w-8">#</th>
                    <th className="text-white/30 text-left py-2 pr-3">Code</th>
                    <th className="text-white/30 text-left py-2 pr-3">Description</th>
                    <th className="text-white/30 text-right py-2 pr-3">Qty</th>
                    <th className="text-white/30 text-right py-2 pr-3">Unit Price</th>
                    <th className="text-white/30 text-right py-2">Ext. Price</th>
                  </tr>
                </thead>
                <tbody>
                  {(order.line_items || []).map((li, idx) => (
                    <tr key={idx} className={`border-b border-white/5 ${li.stock_allocated ? 'bg-cyan-400/5' : ''}`}>
                      <td className="text-white/40 py-2 pr-3">{li.item_no}</td>
                      <td className="text-white/70 font-mono py-2 pr-3">{li.product_code || '—'}</td>
                      <td className="text-white py-2 pr-3">{li.description}</td>
                      <td className="text-white text-right py-2 pr-3">{li.qty}</td>
                      <td className="text-white/70 text-right py-2 pr-3">£{Number(li.unit_price || 0).toFixed(2)}</td>
                      <td className="text-white font-bold text-right py-2">£{Number(li.extended_price || 0).toFixed(2)}</td>
                    </tr>
                  ))}
                </tbody>
                <tfoot>
                  <tr className="border-t border-white/20">
                    <td colSpan={5} className="text-right text-white/50 text-xs py-2 pr-3 font-bold">Subtotal ex. VAT</td>
                    <td className="text-right text-white font-bold text-xs py-2">£{(order.subtotal_ex_vat || 0).toFixed(2)}</td>
                  </tr>
                  <tr>
                    <td colSpan={5} className="text-right text-white/50 text-xs py-1 pr-3">VAT (20%)</td>
                    <td className="text-right text-white/70 text-xs py-1">£{(order.vat_amount || 0).toFixed(2)}</td>
                  </tr>
                  <tr>
                    <td colSpan={5} className="text-right text-white/50 text-xs py-1 pr-3 font-bold">Total inc. VAT</td>
                    <td className="text-right text-white font-bold py-1">£{(order.total_inc_vat || 0).toFixed(2)}</td>
                  </tr>
                </tfoot>
              </table>
            </div>
          </div>

          {order.delivery_address && (
            <div>
              <p className="text-white/30 text-[10px] font-bold tracking-widest mb-1">DELIVERY ADDRESS</p>
              <p className="text-white/60 text-xs whitespace-pre-line">{order.delivery_address}</p>
            </div>
          )}

          {order.source_pdf_url && (
            <a href={order.source_pdf_url} target="_blank" rel="noreferrer"
              className="inline-flex items-center gap-1.5 border border-white/15 hover:border-white/25 text-white/50 hover:text-white font-bold text-[10px] tracking-widest px-3 py-2 transition-colors">
              <ExternalLink className="w-3.5 h-3.5" /> VIEW ORIGINAL PO
            </a>
          )}
        </div>
      )}
    </div>
  );
}